/**
 * VersionSelector
 *
 * Dropdown in the editor toolbar showing version history.
 * Shows which version is deployed, which is latest, and pending changes.
 */

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Clock, Upload, AlertCircle } from 'lucide-react';
import { apiGet } from '../../api/client';

// =============================================================================
// TYPES
// =============================================================================

interface Version {
  id: string;
  number: number;
  createdAt: string;
  createdBy: string | null;
  createdByType: string | null;
  commitMessage: string | null;
  isDeployed: boolean;
  isLatest: boolean;
}

interface VersionsResponse {
  success: boolean;
  websiteStatus: string;
  cloudflareUrl: string | null;
  versions: Version[];
  staging: {
    id: string;
    status: string;
    baseVersionId: string | null;
    updatedAt: string;
    pendingChanges: number;
  } | null;
  deployedVersionId: string | null;
}

interface VersionSelectorProps {
  websiteId: string;
  dirtyCount: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function VersionSelector({ websiteId, dirtyCount }: VersionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<VersionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch versions on mount and when dirtyCount changes (after publish)
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        // Through the shared client so the request carries the session token.
        // This was a bare fetch with no Authorization header, so the moment
        // /api/editor/* went behind the auth gate it 401'd — and the failure
        // branch below rendered as "Loading..." forever, because `!data` kept
        // selecting the loading UI even after loading was false. The toolbar
        // showed an eternal spinner instead of an error, on every site.
        const json = await apiGet<VersionsResponse>(`/api/editor/${websiteId}/versions`);
        setData(json);
        setFailed(false);
      } catch (err) {
        console.error('Failed to fetch versions:', err);
        setFailed(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [websiteId, dirtyCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500">
        <Clock className="w-4 h-4 animate-pulse" />
        <span>Loading...</span>
      </div>
    );
  }

  if (failed || !data) {
    // Say so, quietly. An eternal "Loading..." is a lie about what happened.
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400" title="Version history couldn't be loaded. Refresh to retry.">
        <AlertCircle className="w-4 h-4" />
        <span>Versions unavailable</span>
      </div>
    );
  }

  const latestVersion = data.versions[0];
  const hasPendingChanges = dirtyCount > 0 || (data.staging?.pendingChanges || 0) > 0;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusBadge = () => {
    if (data.websiteStatus === 'live') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          Live
        </span>
      );
    }
    if (data.websiteStatus === 'pending-customer-review') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
          Customer Review
        </span>
      );
    }
    if (data.websiteStatus === 'pending-internal-qa') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
          Internal QA
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
        {data.websiteStatus.replace(/-/g, ' ')}
      </span>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          {hasPendingChanges ? (
            <AlertCircle className="w-4 h-4 text-amber-500" />
          ) : (
            <Clock className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-sm font-medium text-gray-700">
            v{latestVersion?.number || '?'}
          </span>
          {getStatusBadge()}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Version History</span>
              {hasPendingChanges && (
                <span className="text-xs text-amber-600 font-medium">
                  {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Pending changes indicator */}
          {hasPendingChanges && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2 text-sm text-amber-700">
                <AlertCircle className="w-4 h-4" />
                <span>You have unpublished changes</span>
              </div>
            </div>
          )}

          {/* Version list */}
          <div className="max-h-64 overflow-y-auto">
            {data.versions.map((version) => (
              <div
                key={version.id}
                className={`px-3 py-2 border-b border-gray-50 last:border-0 ${
                  version.isLatest ? 'bg-blue-50/50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">v{version.number}</span>
                    {version.isLatest && (
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                        Current
                      </span>
                    )}
                    {version.isDeployed && (
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded flex items-center gap-1">
                        <Upload className="w-3 h-3" />
                        Deployed
                      </span>
                    )}
                  </div>
                  {version.isLatest && !hasPendingChanges && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {formatDate(version.createdAt)}
                  {version.createdByType === 'generation' && ' · Generated'}
                  {version.createdByType === 'publish' && ' · Published'}
                </div>
                {version.commitMessage && (
                  <p className="mt-1 text-xs text-gray-600 truncate">
                    {version.commitMessage}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Footer with deployed URL */}
          {data.cloudflareUrl && data.websiteStatus === 'live' && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <a
                href={data.cloudflareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 truncate block"
              >
                {data.cloudflareUrl}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
